import * as c from '@iamo/custody-lib';
import { IConnection } from '@iamo/custody-lib/build/shared/Connection';
import { IAMORole, IBunkerConsent, IEnrichedTransactionParameters, IMultisigAccount } from './Transaction';

export enum AccountType {
    Unknown,
    OwnLocal,
    Other,
}

export enum Role {
    Owner,
    Approver,
    Challenger,
}
export interface IAccount {
    accountAddress: string;
    privateKey?: string;
    mnemonic?: string;
    ensDomain?: string;
    etherBalanceInWei?: string;
    accountKeyType: AccountType;
    roles?: Role[];
}

export enum AccountCreationStatus {
    Sent = 'sent',
    Error = 'error',
    AlreadyVerified = 'alreadyVerified',
    InvalidTarget = 'invalidTarget',
}

export interface IBunkerKey {
    name: string;
    'key-rights': string;
    value: string;
    'key-type': string;
    metadata: any;
}

/**
 *  Account class for the wallet functionality
 */
export class Account {
    
    // The blockchain property of the backend connection object.
    connection: IConnection

    /**
     * initialized the account class
     * @param connection The backend connection object.
     */
    constructor(connection: IConnection){
        this.connection = connection
    }

    /**
     * Creates a new ethereum account
     * @param entropy Additional entropy for the account creation. 
     * @returns a new IAccount-object
     */
    createAccount(entropy?: string): IAccount {
        return c.Account.createAccount(entropy)
    }

    /**
     * Updates the account type of account object
     * @param account The ethereum account which should be checked.
     * @returns the updated account
     */
    async checkAccountType(account: IAccount):Promise<IAccount> {
        return c.Account.checkAccountType(this.connection.blockchain, account)
    }

    /**
     * Updates the ether balance of account object
     * @param account The ethereum account which should be checked.
     * @returns the updated account
     */
    async checkEtherBalance(account: IAccount): Promise<IAccount>{
        return c.Account.checkEtherBalance(this.connection.blockchain, account)
    }

    /**
     * Creates an account object.
     * @param accountAddress The ethereum account address.
     * @returns the created account object
     */
    async importAccount(accountAddress:string): Promise<IAccount>{
        return c.Account.importAccount(this.connection.blockchain, accountAddress)
    }

    /**
     * Creates an account in the bunker
     * @param phone The phone number of the user
     * @param email The email of the user
     * @param deviceName The device name of the user 
     * @param deviceType The device type
     * @returns an AccountCreation-status
     */
    async createBunkerAccount(
        phone: string,
        email: string,
        deviceName: string,
        deviceType: string
    ):  Promise<{
            signature: {
                timestamp: string;
                value: string;
            };
            status: AccountCreationStatus;
        }> 
    {
        return c.Account.createBunkerAccount(this.connection, phone, email, deviceName, deviceType)
    }

    /**
     * Gets an account from the bunker
     * @returns the account from the bunker
     */
    async getBunkerAccount(): Promise<{ phone: string; email: string }> {
        return c.Account.getBunkerAccount(this.connection)
    }

    /**
     * Creates a new key in the bunker and returns the enrichedTransactionParameters
     * to add the key to the appropriate place on the blockchain.
     * @param instanceAddress The multisig address
     * @param secret Secret for the key
     * @param name Name of the key
     * @param keyRights The rights of the key
     * @param metadata The metadata of the key
     * @param consent The bunker-consent
     * @returns the enriched transaction parameters
     */
    async createBunkerKey(
        instanceAddress: string,
        secret: string,
        name: string,
        keyRights: IAMORole[],
        metadata: any,
        consent: IBunkerConsent
    ): Promise<IEnrichedTransactionParameters> {
        return c.Account.createBunkerKey(this.connection, instanceAddress, secret, name, keyRights, metadata, consent)
    }

    /**
     * Adds an external key to the bunker
     * @param publicKey the publicKey of the key
     * @param name the name of the key
     * @param keyRights the key-rights of the key
     * @param metadata the metadata of the key
     * @param consent the bunker-consent
     * @returns the public-key
     */
    async addExternalKeyToBunker(
        publicKey: string,
        name: string,
        keyRights: IAMORole[],
        metadata: any,
        consent: IBunkerConsent
    ): Promise<{ 'public-key': string }> {
        return c.Account.addExternalKeyToBunker(this.connection, publicKey, name, keyRights, metadata, consent)
    }

    /**
     * Gets all bunker keys associated with the user key
     * @returns all bunker keys associated with the user key
     */
    async getBunkerKeys(): Promise<IBunkerKey[]> {
        return c.Account.getBunkerKeys(this.connection)
    }

    /**
     * Takes all keys in the safe and modules and moves them to the bunker
     * @param instanceAddress The multisig instance address
     * @returns An array with the multisig-accounts
     */
    async migrateKeysToBunker(instanceAddress: string): Promise<IMultisigAccount[]> {
        return c.Account.migrateKeysToBunker(this.connection, instanceAddress)
    }
}

export default Account

    /*
    TODO:
    sdk.Wallet.Account.addExternalKeyToBunker()
    sdk.Wallet.Account.createBunkerAccount()
    sdk.Wallet.Account.createBunkerKey()
    sdk.Wallet.Account.getBunkerAccount()
    sdk.Wallet.Account.getBunkerKeys(),
    sdk.Wallet.Account.listDevices()
    sdk.Wallet.Account.migrateKeysToBunker()
    */